import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { brand, marketing, radii } from '@lunarcrush/ds/tokens';

// Locale catalog mirrors `apps/app/src/lib/i18n/locales.ts` so the cookie
// we write here (NEXT_LOCALE) is a valid value for apps/app's i18n
// resolution chain (DEV-10435: cookie → geo → en).
const LOCALES = [
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'es', name: 'Español', short: 'ES' },
  { code: 'de', name: 'Deutsch', short: 'DE' },
  { code: 'fr', name: 'Français', short: 'FR' },
  { code: 'zh', name: '中文', short: 'ZH' },
] as const;

type LocaleCode = (typeof LOCALES)[number]['code'];

const COOKIE_NAME = 'NEXT_LOCALE';
const STORAGE_KEY = 'lc.publicLocale';

function readLocale(): LocaleCode {
  if (typeof document === 'undefined') return 'en';
  // Cookie takes priority — apps/app writes it on its own surface, so a
  // signed-in user who switched in the app and then visits the public
  // site sees the same selection.
  const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  const fromCookie = m?.[1] as LocaleCode | undefined;
  if (fromCookie && LOCALES.some((l) => l.code === fromCookie)) return fromCookie;
  const fromStorage = localStorage.getItem(STORAGE_KEY) as LocaleCode | null;
  if (fromStorage && LOCALES.some((l) => l.code === fromStorage)) return fromStorage;
  return 'en';
}

function writeLocale(code: LocaleCode) {
  if (typeof document === 'undefined') return;
  // Persist to both surfaces:
  //   - NEXT_LOCALE cookie at the apex domain so app.lunarcrush.com (or
  //     wherever apps/app is mounted) picks it up via its existing
  //     resolution chain on the next request.
  //   - localStorage as a fallback for the public site itself — useful
  //     once we start translating UI strings here (next ticket).
  const host = window.location.hostname;
  const isApex = host.endsWith('lunarcrush.com');
  const domainAttr = isApex ? '; domain=.lunarcrush.com' : '';
  document.cookie = `${COOKIE_NAME}=${code}; path=/; max-age=31536000; samesite=lax${domainAttr}`;
  localStorage.setItem(STORAGE_KEY, code);
}

interface LanguagePickerProps {
  /** `compact` = navbar variant (icon + short code). `full` = drawer variant (native names). */
  variant?: 'compact' | 'full';
}

const GlobeIcon: React.FC<{ size?: number; color: string }> = ({ size = 16, color }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill={color} aria-hidden>
    <path d="M128,24A104,104,0,1,0,232,128,104.12,104.12,0,0,0,128,24Zm88,104a87.61,87.61,0,0,1-3.33,24H174.16a157.44,157.44,0,0,0,0-48h38.51A87.61,87.61,0,0,1,216,128ZM102,168H154a115.11,115.11,0,0,1-26,45A115.27,115.27,0,0,1,102,168Zm-3.9-16a140.84,140.84,0,0,1,0-48h59.88a140.84,140.84,0,0,1,0,48ZM40,128a87.61,87.61,0,0,1,3.33-24H81.84a157.44,157.44,0,0,0,0,48H43.33A87.61,87.61,0,0,1,40,128ZM154,88H102a115.11,115.11,0,0,1,26-45A115.27,115.27,0,0,1,154,88Zm52.33,0H170.71a135.28,135.28,0,0,0-22.3-45.6A88.29,88.29,0,0,1,206.37,88ZM107.59,42.4A135.28,135.28,0,0,0,85.29,88H49.63A88.29,88.29,0,0,1,107.59,42.4ZM49.63,168H85.29a135.28,135.28,0,0,0,22.3,45.6A88.29,88.29,0,0,1,49.63,168Zm98.78,45.6a135.28,135.28,0,0,0,22.3-45.6h35.66A88.29,88.29,0,0,1,148.41,213.6Z" />
  </svg>
);

export const LanguagePicker: React.FC<LanguagePickerProps> = ({ variant = 'compact' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<LocaleCode>('en');
  const [hovered, setHovered] = useState(false);
  // Mobile gets a bottom sheet instead of an absolute-positioned
  // dropdown. The trigger lives inside the MobileNav drawer, so a
  // `right: 0` dropdown pushed the options off-screen. Track viewport
  // via matchMedia so the right surface mounts on resize too.
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Sync the trigger label with the active i18n language. Cookie/localStorage
  // are still the source of truth for cross-domain handoff (apps/app reads
  // NEXT_LOCALE on the next request), so we read once on mount and then
  // follow i18n.language as the user switches.
  useEffect(() => {
    const initial = readLocale();
    setLocale(initial);
    if (i18n.language !== initial) {
      i18n.changeLanguage(initial);
    }
  }, [i18n]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 1023.98px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!open || isMobile) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, isMobile]);

  // Lock body scroll while the mobile sheet is open and close on ESC,
  // matching the rest of the app's modal patterns.
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, isMobile]);

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  const handlePick = (code: LocaleCode) => {
    setLocale(code);
    writeLocale(code);
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Trigger color/state mirrors the secondary nav button: muted body text by
  // default, white on hover/open, with the same Plus Jakarta Sans typeface.
  const triggerColor = open || hovered ? marketing.textHeading : marketing.textBody;
  const isCompact = variant === 'compact';

  return (
    <div
      ref={ref}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.name}`}
        style={{
          background: 'transparent',
          border: 'none',
          color: triggerColor,
          // DEV-11282 re-work: compact variant slims its horizontal padding —
          // the invisible hit area read as extra spacing in the navbar.
          padding: isCompact ? '8px 4px' : '10px 14px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 14,
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: 500,
          transition: 'color 200ms ease',
        }}
      >
        <GlobeIcon size={isCompact ? 16 : 18} color={triggerColor} />
        <span>{isCompact ? current.short : current.name}</span>
      </button>
      {open && !isMobile && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            background: marketing.card,
            border: `1px solid ${marketing.border}`,
            borderRadius: radii.md,
            padding: '8px 0',
            margin: 0,
            listStyle: 'none',
            minWidth: 180,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 200,
          }}
        >
          {LOCALES.map((l) => {
            const active = l.code === locale;
            return (
              <li key={l.code}>
                <LocaleRow
                  label={l.name}
                  active={active}
                  onPick={() => handlePick(l.code)}
                />
              </li>
            );
          })}
        </ul>
      )}
      {open && isMobile && (
        <>
          {/* Backdrop — closes the sheet on tap. The sheet itself sits
              above this on z-index. */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
            aria-hidden
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Select language"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              background: marketing.card,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderTop: `1px solid ${marketing.border}`,
              boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
              zIndex: 1001,
              paddingTop: 8,
              paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
              maxHeight: '85dvh',
              overflowY: 'auto',
            }}
          >
            {/* Grabber */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 8 }}>
              <span
                style={{
                  display: 'block',
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  background: marketing.textBody,
                  opacity: 0.3,
                }}
              />
            </div>
            <ul
              role="listbox"
              style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
              {LOCALES.map((l) => {
                const active = l.code === locale;
                return (
                  <li key={l.code}>
                    <LocaleRow
                      label={l.name}
                      active={active}
                      onPick={() => handlePick(l.code)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

const LocaleRow: React.FC<{
  label: string;
  active: boolean;
  onPick: () => void;
}> = ({ label, active, onPick }) => {
  const [rowHovered, setRowHovered] = useState(false);
  const color = active || rowHovered ? marketing.textHeading : marketing.textBody;
  return (
    <button
      type="button"
      onClick={onPick}
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
      aria-selected={active}
      style={{
        background: rowHovered ? marketing.cardHighlight : 'transparent',
        border: 'none',
        color,
        width: '100%',
        textAlign: 'left',
        padding: '10px 24px',
        cursor: 'pointer',
        fontSize: 14,
        fontFamily: 'Plus Jakarta Sans',
        fontWeight: active ? 600 : 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        transition: 'background-color 200ms ease, color 200ms ease',
      }}
    >
      <span>{label}</span>
      {active && (
        <span style={{ color: brand.purple, fontSize: 14 }} aria-hidden>
          ●
        </span>
      )}
    </button>
  );
};
